import { captureError } from '@smg-real-estate/browser-rum';
import { type SmgSplitSession } from '@smg-real-estate/vue-experiments';
import type SplitIO from '@splitsoftware/splitio/types/splitio';
import { defineStore } from 'pinia';

interface TreatmentConfig {
  treatment: string;
  config?: unknown;
}

interface SplitState {
  treatments: {
    [key: string]: TreatmentConfig;
  };
  splitSessionKey: string;
}

export interface TreatmentPayload {
  featureFlagName: string;
  treatmentWithConfig: SplitIO.TreatmentWithConfig;
}

export const defaultSmgSplitSession: SmgSplitSession = {
  splitSessionKey: '',
  splitTreatmentsWithConfig: {},
};

const useSplitStore = defineStore('splitStore', {
  state: (): SplitState => ({
    treatments: {},
    splitSessionKey: '',
  }),
  actions: {
    updateSplitStoreFromApi(smgSplitSession: SmgSplitSession | undefined): void {
      if (!smgSplitSession) {
        captureError(new Error('SplitIO session not initialized'));
        return;
      }

      this.insertTreatmentsIntoStore(smgSplitSession.splitTreatmentsWithConfig);
      this.setSplitSessionKey(smgSplitSession.splitSessionKey);
    },
    insertTreatmentsIntoStore(treatmentsWithConfig: SplitIO.TreatmentsWithConfig): void {
      Object.entries(treatmentsWithConfig).forEach(([featureFlagName, treatmentWithConfig]) => {
        this.setTreatment({ featureFlagName, treatmentWithConfig });
      });
    },

    setTreatment({ featureFlagName, treatmentWithConfig }: TreatmentPayload): void {
      const { treatment, config } = treatmentWithConfig;
      const treatmentConfig = {
        treatment,
        config: config ? JSON.parse(config) : config,
      };

      this.treatments = {
        ...this.treatments,
        [featureFlagName]: treatmentConfig,
      };
    },
    setSplitSessionKey(splitSessionKey: string): void {
      this.splitSessionKey = splitSessionKey;
    },
    isFeatureOn(featureFlagName: string): boolean {
      const treatmentConfig = this.treatments[featureFlagName];
      if (!treatmentConfig) {
        return false;
      }
      return treatmentConfig.treatment === 'on';
    },
  },
});

export default useSplitStore;
